<template>
    <div class="w-full pb-14 lg:pb-0 md:lg:w-[387px]">
        <span class="styled-as-h3 mb-4 text-sm lg:text-xl">
            {{ snippets['headless.footer.newsletterTitle'] }}
        </span>
        <p class="mb-3 text-sm">
            {{ snippets['headless.footer.subscribeToNewsletter'] }}
        </p>
        <p class="mb-5 text-sm leading-normal lg:mb-7 lg:w-96">
            {{ snippets['headless.footer.newsletterDescription'] }}
        </p>
        <form class="flex flex-col" @submit.prevent="submitRegisterNewsletter">
            <div class="flex flex-col md:flex-row">
                <div class="w-full">
                    <label class="mb-1 leading-none md:hidden" for="newsletter-email">
                        {{ snippets['headless.footer.email'] }}
                    </label>
                    <input
                        id="newsletter-email"
                        class="input mb-2 w-full bg-white lg:mb-0 lg:w-[215px]"
                        type="email"
                        name="email"
                        autocomplete="email"
                        required
                        v-model="newsletterForm.email"
                        :placeholder="snippets['headless.footer.email']"
                        :disabled="isSubmitting"
                        :aria-invalid="!!emailError || (newsletterForm.result.success === false && newsletterForm.result.message !== '')"
                        :aria-describedby="emailError ? 'newsletter-email-error' : undefined" />
                    <p id="newsletter-email-error" class="mt-1 text-xs text-red-600" role="alert" v-if="emailError">
                        {{ emailError }}
                    </p>
                </div>
                <div class="w-full md:w-fit lg:w-full">
                    <button
                        type="submit"
                        class="btn btn-primary flex w-full items-center justify-center gap-2 text-center !font-normal md:w-fit lg:w-full"
                        :disabled="isSubmitting"
                        :aria-busy="isSubmitting">
                        <IconSpinner
                            v-if="isSubmitting"
                            class="size-4 shrink-0 animate-spin"
                            aria-hidden="true" />
                        <span>{{ snippets['headless.footer.register'] }}</span>
                    </button>
                </div>
            </div>
        </form>
        <p class="mt-3 text-sm" v-if="newsletterForm.result.success && newsletterForm.result.message">
            {{ snippets['headless.newsletter.succes'] }}
        </p>
        <template v-else-if="newsletterForm.result.message === ''"> </template>
        <p class="mt-3 text-sm" v-else>
            {{ snippets['headless.newsletter.failed'] }}
        </p>
    </div>
</template>

<script lang="ts" setup>
    import { ref, watch } from 'vue';
    import { useApiClient } from '~/src/apiClient';

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const newsletterForm = ref({
        email: '',
        result: {
            message: '',
            success: false,
        },
    });

    const isSubmitting = ref(false);

    /** Canonical email pattern (matches `ui/input/Email.vue`); the trailing `{2,}` enforces a TLD of at least 2 characters. */
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    const emailError = ref('');

    // Clear the validation error as soon as the user edits the field again.
    watch(
        () => newsletterForm.value.email,
        () => {
            if (emailError.value) {
                emailError.value = '';
            }
        },
    );

    async function submitRegisterNewsletter() {
        if (isSubmitting.value) {
            return;
        }
        const email = newsletterForm.value.email.trim();
        if (!emailPattern.test(email)) {
            emailError.value = snippets['headless.input.emailInvalid'] || 'Vul een geldig e-mailadres in.';
            return;
        }
        emailError.value = '';
        isSubmitting.value = true;
        try {
            const { apiClient } = useApiClient();
            const data = await apiClient.invoke('subscribe post /nd/newsletter/subscribe', {
                body: {
                    email,
                },
            });
            newsletterForm.value.result = data.data;
        } catch (err) {
            newsletterForm.value.result.message = 'failed';
            newsletterForm.value.result.success = false;
        } finally {
            isSubmitting.value = false;
        }
    }
</script>
